import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { useUser } from "../contexts/UserContext";
import wordpressApi from "../utils/wordpressApi";
import config from "../configs/config";
import { Button } from "./Button";
import { URL_PATHS } from "../utils/routeUtils";
import { CreditOptionsList } from "./CreditOptionsMenu";

// Import all icons from assets
import guidesIcon from "../assets/icons/guides-icon.svg";
import creditIcon from "../assets/icons/credit-icon.svg";
import benefitsIcon from "../assets/icons/benefits-icon.svg";
import discountsIcon from "../assets/icons/discounts-icon.svg";
import closeIcon from "../assets/icons/close-icon.svg";
import hamburgerIcon from "../assets/icons/hamburger-icon.svg";
import mobileLinkIcon from "../assets/icons/mobile-link-icon.svg";
import affordaLogo from "../assets/afforda-logo.svg";

interface MenuItem {
  title: string;
  url: string;
}

interface MenuItems {
  [key: string]: MenuItem[];
}

interface HeaderProps {}

interface Section {
  title: string;
  key: string;
}

interface MainMenuItem {
  title: string;
  key?: string;
  url?: string;
  isFromWPList?: boolean;
  sections?: Section[];
}

const Header: React.FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { clearUserData } = useUser();
  const [menuItems, setMenuItems] = useState<MenuItems>({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [isCreditMenuOpen, setIsCreditMenuOpen] = useState(false);
  const creditMenuRef = useRef<HTMLDivElement>(null);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["Guides"]);
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const isLeadLandingPage = normalizedPath === URL_PATHS.LEAD_LANDING;
  const isExtLandingPage = normalizedPath === URL_PATHS.EXT_LANDING;
  const isLandingHeader = isLeadLandingPage || isExtLandingPage;

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // Close the desktop credit menu when clicking outside it
  useEffect(() => {
    if (!isCreditMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        creditMenuRef.current &&
        !creditMenuRef.current.contains(event.target as Node)
      ) {
        setIsCreditMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCreditMenuOpen]);

  // Close desktop menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDesktopMenuOpen) {
        const target = event.target as Element;
        const desktopMenuContainer = document.querySelector(
          ".hidden.custom\\:flex .relative",
        );

        if (desktopMenuContainer && !desktopMenuContainer.contains(target)) {
          setIsDesktopMenuOpen(false);
        }
      }
    };

    if (isDesktopMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDesktopMenuOpen]);

  useEffect(() => {
    if (isLandingHeader) {
      return;
    }

    const fetchMenuItems = async () => {
      const items = await wordpressApi.fetchAllSubMenuItems();
      setMenuItems(items);
    };
    fetchMenuItems();
  }, [isLandingHeader]);

  const handleAuthAction = () => {
    setIsMobileMenuOpen(false);
    if (user) {
      signOut();
      clearUserData();
      navigate("/");
    } else {
      navigate("/");
    }
  };

  const handleSettingsClick = () => {
    setIsMobileMenuOpen(false);
    setIsDesktopMenuOpen(false);
    navigate("/preferences");
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // When opening mobile menu, set Guides as expanded by default
    setExpandedMenus((prev) => (isMobileMenuOpen ? [] : ["Guides"]));
  };

  const toggleDesktopMenu = () => {
    setIsDesktopMenuOpen(!isDesktopMenuOpen);
  };

  const handleDesktopMenuAction = (action: () => void) => {
    setIsDesktopMenuOpen(false);
    action();
  };

  const mainMenuItems: MainMenuItem[] = [
    {
      title: "Guides",
      key: "Guides",
      sections: [
        { title: "Benefits", key: "Benefits" },
        { title: "Personal finance", key: "Personal Finance" },
        { title: "Loans", key: "Loans" },
        { title: "Insights", key: "Insights" },
      ],
    },
    {
      title: "FAQs",
      url: `${config.AFFORDA_STATIC_SITE}/faqs/`,
      isFromWPList: true,
    },
    {
      title: "Contact us",
      url: `/contact-us`,
    },
  ];

  if (isLandingHeader) {
    return (
      <header className="custom:top-8 top-0 left-0 right-0 z-50 bg-grey-20">
        <div className="custom:py-6 py-4 relative">
          <div className="container-header flex items-center">
            <img src={affordaLogo} alt="Afforda Logo" className="h-9" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className="custom:top-8 top-0 left-0 right-0 z-50 bg-grey-20">
        <div className="custom:py-6 py-4 relative">
          <div className="container-header">
            {/* Left section: Logo + Guides */}
            <div className="flex items-center space-x-6 relative">
              <a href="/customer-home" className="flex-shrink-0">
                <img src={affordaLogo} alt="Afforda Logo" className="h-9" />
              </a>
              {/* Guides button */}
              <div className="hidden custom:block relative group">
                <button
                  className="bg-white text-black border-[1.5px] border-grey-20 hover:border-grey-60 px-4 py-3 text-sm md:px-[18px] md:py-3 md:text-[17px] rounded-30 font-semibold transition-all duration-200 focus:outline-none focus:ring focus:ring-black focus:ring-offset-2 min-w-[100px] text-center inline-flex items-center group"
                  aria-expanded={expandedMenus.includes("Guides")}
                  aria-haspopup="true"
                >
                  <img src={guidesIcon} alt="" className="w-4 h-4 mr-1" />
                  Guides
                  <svg
                    className="w-5 h-5 ml-1.5 transition-transform duration-200 group-hover:rotate-180 text-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {/* Transparent bridge to maintain hover - Desktop only */}
                <div
                  className="hidden custom:block absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible z-40"
                  style={{
                    top: "100%",
                    left: "calc(-11rem)", // Account for container padding to align with logo
                    width: "min(969px, calc(100vw - 40px))",
                    height: "8px",
                  }}
                />
                {/* Guides dropdown - Desktop only */}
                <div
                  className="hidden custom:block absolute bg-white border border-black rounded-20 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 ease-in-out z-50"
                  role="menu"
                  aria-label="Desktop submenu"
                  style={{
                    top: "calc(100% + 8px)",
                    left: "calc(-11rem)", // Account for container padding to align with logo
                    width: "min(969px, calc(100vw - 40px))",
                    maxWidth: "calc(100vw - 40px)",
                    height: "437px",
                  }}
                  onMouseEnter={() => {
                    document.body.style.overflow = "hidden";
                  }}
                  onMouseLeave={() => {
                    document.body.style.overflow = "";
                  }}
                >
                  {/* Inner content frame with 28px padding */}
                  <div className="p-7 h-full">
                    <div className="flex flex-row items-start gap-3 h-full">
                      {/* Main content container */}
                      <div className="flex flex-row items-start gap-4 w-full h-full">
                        {mainMenuItems
                          .find((item) => item.key === "Guides")
                          ?.sections?.map((section, index) => (
                            <React.Fragment key={section.key}>
                              {/* Column */}
                              <div className="flex flex-row justify-center items-start flex-1">
                                <div className="flex flex-row items-start gap-3 w-full h-full">
                                  <div className="flex flex-col items-start gap-3 w-full h-full">
                                    {/* Section header */}
                                    <p className="text-xs font-semibold leading-[135%] tracking-[0.1em] uppercase text-grey-80">
                                      {section.title}
                                    </p>

                                    {/* Links container */}
                                    <div className="flex flex-row items-start gap-9 w-full flex-1">
                                      <div className="flex flex-col items-start gap-2 w-full flex-1">
                                        <div className="flex flex-col items-start gap-3 w-full flex-1">
                                          {/* Scrollable links list */}
                                          <div className="w-full flex-1 overflow-hidden">
                                            <ul
                                              className="space-y-3 max-h-[348px] overflow-y-auto pr-2"
                                              aria-label={`${section.title} section submenu`}
                                              onMouseEnter={(e) => {
                                                const el = e.currentTarget;
                                                el.style.overflowY = "auto";
                                              }}
                                              onMouseLeave={(e) => {
                                                const el = e.currentTarget;
                                                el.style.overflowY = "hidden";
                                              }}
                                              onWheel={(e) => {
                                                e.stopPropagation();
                                              }}
                                            >
                                              {menuItems[section.key]?.map(
                                                (subItem: MenuItem) => (
                                                  <li key={subItem.title}>
                                                    {/* eslint-disable-next-line */}
                                                    <a
                                                      href={`${config.AFFORDA_STATIC_SITE}${subItem.url}`}
                                                      target="_blank"
                                                      className="block text-sm font-semibold leading-[140%] tracking-[0.02em] text-black hover:text-blue-700 hover:underline transition-colors duration-200"
                                                    >
                                                      {subItem.title}
                                                    </a>
                                                  </li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Vertical separator (except after last column) */}
                              {index <
                                (mainMenuItems.find(
                                  (item) => item.key === "Guides",
                                )?.sections?.length || 0) -
                                  1 && (
                                <div className="flex items-center justify-center h-full">
                                  <div
                                    className="bg-grey-20"
                                    style={{
                                      width: "2px",
                                      height: "348px",
                                    }}
                                  />
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right section: Credit, Benefits, Offers, Hamburger Menu */}
            <div className="hidden custom:flex items-center space-x-4">
              {user && (
                <>
                  <div className="relative" ref={creditMenuRef}>
                    <Button
                      text="Credit"
                      variant="nav"
                      onClick={() => setIsCreditMenuOpen((open) => !open)}
                      icon={creditIcon}
                      iconAlt="Credit"
                      className={`text-sm ${
                        isCreditMenuOpen ? "!border-grey-60" : ""
                      }`}
                      ariaPressed={isCreditMenuOpen}
                    />

                    {/* Desktop credit options dropdown */}
                    {isCreditMenuOpen && (
                      <div
                        className="absolute left-0 top-full mt-2 w-[360px] bg-primary rounded-2xl shadow-lg z-50 px-6 py-8 border border-black"
                        role="menu"
                        aria-label="Credit options"
                        data-testid="credit-options-dropdown"
                      >
                        <CreditOptionsList
                          onSelect={() => setIsCreditMenuOpen(false)}
                          gapClassName="gap-8"
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    text="Benefits"
                    variant="nav"
                    onClick={() => navigate("/getting-started")}
                    icon={benefitsIcon}
                    iconAlt="Benefits"
                    className="text-sm"
                  />
                  <Button
                    text="Discounts"
                    variant="nav"
                    onClick={() => navigate("/discounts")}
                    icon={discountsIcon}
                    iconAlt="Discounts"
                    className="text-sm"
                  />
                </>
              )}
              <div className="relative">
                <Button
                  text=""
                  variant="nav"
                  onClick={toggleDesktopMenu}
                  icon={isDesktopMenuOpen ? closeIcon : hamburgerIcon}
                  iconAlt={isDesktopMenuOpen ? "Close" : "Menu"}
                  title="Toggle menu"
                  className={`!min-w-0 !px-3 !py-3 [&>div]:!gap-0 [&>div]:!px-2 ${
                    isDesktopMenuOpen ? "!bg-primary !text-white" : ""
                  }`}
                />

                {/* Desktop dropdown menu */}
                {isDesktopMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-60 h-96 bg-primary rounded-2xl shadow-lg z-50 p-6 border border-black">
                    <div className="flex flex-col space-y-10">
                      {/* Ask Afforda */}
                      {user && (
                        <button
                          onClick={() =>
                            handleDesktopMenuAction(() =>
                              navigate("/ask-afforda"),
                            )
                          }
                          className="flex items-center space-x-3 text-left text-black hover:text-gray-700 transition-colors duration-200"
                        >
                          <img
                            src={mobileLinkIcon}
                            alt=""
                            className="w-6 h-6 flex-shrink-0"
                            style={{ transform: "rotate(1.47deg)" }}
                          />
                          <span className="text-lg font-bold">Ask Afforda</span>
                        </button>
                      )}

                      {/* Support */}
                      <button
                        onClick={() =>
                          handleDesktopMenuAction(() =>
                            window.open(
                              `${config.AFFORDA_STATIC_SITE}/faqs/`,
                              "_blank",
                            ),
                          )
                        }
                        className="flex items-center space-x-3 text-left text-black hover:text-gray-700 transition-colors duration-200"
                      >
                        <img
                          src={mobileLinkIcon}
                          alt=""
                          className="w-6 h-6 flex-shrink-0"
                          style={{ transform: "rotate(100.72deg)" }}
                        />
                        <span className="text-lg font-bold">FAQs</span>
                      </button>

                      {/* Settings */}
                      {user && (
                        <button
                          onClick={() =>
                            handleDesktopMenuAction(() =>
                              navigate("/preferences"),
                            )
                          }
                          className="flex items-center space-x-3 text-left text-black hover:text-gray-700 transition-colors duration-200"
                        >
                          <img
                            src={mobileLinkIcon}
                            alt=""
                            className="w-6 h-6 flex-shrink-0"
                            style={{ transform: "rotate(49.78deg)" }}
                          />
                          <span className="text-lg font-bold">Settings</span>
                        </button>
                      )}

                      {/* Contact */}
                      <button
                        onClick={() =>
                          handleDesktopMenuAction(() => navigate("/contact-us"))
                        }
                        className="flex items-center space-x-3 text-left text-black hover:text-gray-700 transition-colors duration-200"
                      >
                        <img
                          src={mobileLinkIcon}
                          alt=""
                          className="w-6 h-6 flex-shrink-0"
                          style={{ transform: "rotate(-90deg)" }}
                        />
                        <span className="text-lg font-bold">Contact</span>
                      </button>
                    </div>

                    {/* Sign out */}
                    {user && (
                      <div className="mt-12">
                        <button
                          onClick={() =>
                            handleDesktopMenuAction(() => {
                              signOut();
                              clearUserData();
                              navigate("/");
                            })
                          }
                          className="text-black underline font-medium hover:text-gray-700 transition-colors duration-200"
                        >
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex custom:hidden">
              <Button
                text=""
                variant="nav"
                onClick={toggleMobileMenu}
                icon={hamburgerIcon}
                iconAlt="Menu"
                title="Toggle mobile menu"
                className="!min-w-0 !px-3 !py-3 [&>div]:!gap-0 [&>div]:!px-2"
              />
            </div>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div
            className="custom:hidden fixed inset-0 z-50"
            role="dialog"
            aria-label="Mobile menu"
          >
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity duration-300"
              onClick={toggleMobileMenu}
              role="button"
              aria-label="Close mobile menu"
            ></div>
            <div
              className={`relative h-full w-full bg-grey-20 shadow-xl flex flex-col transform transition-opacity duration-300 ease-in-out ${
                isMobileMenuOpen
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none"
              }`}
            >
              {/* Header section with logo and close button */}
              <div className="flex justify-between items-center pt-4 px-4 pb-4 bg-grey-20">
                <a href="/customer-home" className="flex-shrink-0">
                  <img src={affordaLogo} alt="Afforda Logo" className="h-9" />
                </a>
                <Button
                  text=""
                  variant="nav"
                  onClick={toggleMobileMenu}
                  icon={closeIcon}
                  iconAlt="Close"
                  title="Close menu"
                  className="!min-w-0 !px-3 !py-3 [&>div]:!gap-0 [&>div]:!px-2"
                />
              </div>

              {/* Blue Menu Content Section */}
              <div className="flex-1 p-5 pt-0">
                <div className="flex flex-col justify-center items-start pt-[30px] px-[34px] gap-[33px] h-full max-h-[calc(100vh-120px)] w-full relative bg-primary rounded-30 overflow-y-auto">
                  <div className="flex flex-col justify-center items-start gap-[49px] w-full">
                    {/* Ask Afforda */}
                    {user && (
                      <button
                        onClick={() => {
                          navigate("/ask-afforda");
                          toggleMobileMenu();
                        }}
                        className="flex flex-row items-center w-full"
                      >
                        <div className="w-[46px] flex justify-center items-center flex-shrink-0">
                          <img
                            src={mobileLinkIcon}
                            alt=""
                            className="w-[30.59px] h-[26.48px]"
                            style={{ transform: "rotate(1.47deg)" }}
                          />
                        </div>
                        <span className="font-extrabold text-2xl md:text-3xl leading-[90%]">
                          Ask Afforda
                        </span>
                      </button>
                    )}

                    {/* Support */}
                    <button
                      onClick={() => {
                        window.open(
                          `${config.AFFORDA_STATIC_SITE}/faqs/`,
                          "_blank",
                        );
                        toggleMobileMenu();
                      }}
                      className="flex flex-row items-center w-full"
                    >
                      <div className="w-[46px] flex justify-center items-center flex-shrink-0">
                        <img
                          src={mobileLinkIcon}
                          alt=""
                          className="w-[30.59px] h-[26.48px]"
                          style={{ transform: "rotate(100.72deg)" }}
                        />
                      </div>
                      <span className="font-extrabold text-2xl md:text-3xl leading-[90%]">
                        FAQs
                      </span>
                    </button>

                    {/* Settings */}
                    {user && (
                      <button
                        onClick={() => {
                          handleSettingsClick();
                          toggleMobileMenu();
                        }}
                        className="flex flex-row items-center w-full"
                      >
                        <div className="w-[46px] flex justify-center items-center flex-shrink-0">
                          <img
                            src={mobileLinkIcon}
                            alt=""
                            className="w-[30.59px] h-[26.48px]"
                            style={{ transform: "rotate(49.78deg)" }}
                          />
                        </div>
                        <span className="font-extrabold text-2xl md:text-3xl leading-[90%]">
                          Settings
                        </span>
                      </button>
                    )}

                    {/* Contact */}
                    <button
                      onClick={() => {
                        navigate("/contact-us");
                        toggleMobileMenu();
                      }}
                      className="flex flex-row items-center w-full"
                    >
                      <div className="w-[46px] flex justify-center items-center flex-shrink-0">
                        <img
                          src={mobileLinkIcon}
                          alt=""
                          className="w-[30.59px] h-[26.48px]"
                          style={{ transform: "rotate(-90deg)" }}
                        />
                      </div>
                      <span className="font-extrabold text-2xl md:text-3xl leading-[90%]">
                        Contact
                      </span>
                    </button>
                  </div>

                  {/* Sign out - positioned at bottom */}
                  {user && (
                    <button
                      onClick={handleAuthAction}
                      className="absolute bottom-6 right-[34px] font-semibold text-base leading-[135%] underline"
                    >
                      Sign out
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
